import React from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { colors } from '../../style';

const RADIO_SIZES = {
  sm: { outer: '16px', inner: '8px' },
  md: { outer: '20px', inner: '10px' },
} as const;

export type RadioSize = keyof typeof RADIO_SIZES;

export interface RadioProps {
  className?: string;
  active?: boolean;
  disabled?: boolean;
  checked?: boolean;
  size?: RadioSize;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const RadioComponent = ({ className, active, disabled, ...rest }: RadioProps) => (
  <div className={classNames(className, { active }, { disabled })} {...rest}>
    <span className="hover-ripple" />
    <span className="active-ripple" />
  </div>
);

const Radio = styled(RadioComponent).withConfig({
  shouldForwardProp: prop => !['checked', 'size'].includes(prop),
})<RadioProps>`
  --radio-outer: ${props => RADIO_SIZES[props.size ?? 'md'].outer};
  --radio-inner: ${props => RADIO_SIZES[props.size ?? 'md'].inner};
  position: relative;
  cursor: pointer;
  transition: border-color 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0 calc(var(--radio-outer) / 1.5);
  min-height: calc(var(--radio-outer) + 2px);
  &:before,
  &:after {
    box-sizing: border-box;
    position: absolute;
    content: '';
    border-radius: 50%;
    transition: all 0.3s ease;
    transition-property: transform, border-color;
  }
  &:after {
    left: 0;
    top: 0;
    height: var(--radio-outer);
    width: var(--radio-outer);
    border: 2px solid;
    border-color: ${props => (props.checked ? props.theme.actionColor : colors.gray600)};
  }
  &:before {
    content: '';
    position: absolute;
    top: calc((var(--radio-outer) - var(--radio-inner)) / 2);
    left: calc((var(--radio-outer) - var(--radio-inner)) / 2);
    height: var(--radio-inner);
    width: var(--radio-inner);
    border-radius: 50%;
    transform: ${props => (props.checked ? 'scale(1,1)' : 'scale(0,0)')};
    background: ${props => props.theme.actionColor};
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  }
  &.disabled {
    cursor: not-allowed;

    &:after {
      border-color: ${colors.gray600};
    }
    &:before {
      background: ${colors.gray600};
    }
  }

  .hover-ripple,
  .active-ripple {
    position: absolute;
    top: calc(-1 * var(--radio-outer) / 4);
    left: calc(-1 * var(--radio-outer) / 4);
    opacity: 0;
    display: block;
    border-radius: 50%;
    height: calc(var(--radio-outer) * 1.5);
    width: calc(var(--radio-outer) * 1.5);
  }
  .hover-ripple {
    background: ${colors.gray300};
  }
  &:hover:not(.disabled):not(&.active) {
    .hover-ripple {
      opacity: 0.5;
    }
  }
  .active-ripple {
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    transform: scale(0, 0);
    background: ${props => props.theme.actionColor};
  }
  &:active:not(.disabled),
  &:focus:not(.disabled),
  &.active {
    .active-ripple {
      opacity: 0.3;
      transform: scale(1, 1);
    }
  }
`;

export default Radio;
